import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";

/**
 * ValuationTypeDropdown Component
 *
 * Displays a dropdown menu with different valuation types for creation.
 * Provides template selection for creating new valuations.
 */
const ValuationTypeDropdown = ({
  buttonClassName = "",
  dropdownClassName = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Valuation type options
  const valuationTypes = [
    {
      id: "basic",
      label: "Basic Property Valuation",
      description: "Standard property development and investment valuation",
      enabled: true,
      template: "basic",
    },
    {
      id: "step-one",
      label: "Step One Property Valuation Report v1",
      description:
        "Template that mirrors Step One Finance Mortgage Valuation Reports (Form V1)",
      enabled: true,
      template: "step-one",
    },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close dropdown when an option is selected
  const handleOptionSelect = () => {
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors ${buttonClassName}`}
      >
        <PlusIcon className="h-5 w-5" />
        <span>New Valuation</span>
        <ChevronDownIcon
          className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 ${dropdownClassName}`}
        >
          <div className="py-2">
            <div className="px-4 py-2 border-b border-gray-100">
              <h3 className="text-sm font-medium text-gray-900">
                Select Valuation Type
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Choose the type of valuation you want to create
              </p>
            </div>

            {valuationTypes.map((type) => (
              <div key={type.id} className="px-2">
                {type.enabled ? (
                  <Link
                    to={`/new-valuation?template=${type.template}`}
                    onClick={handleOptionSelect}
                    className="flex flex-col px-3 py-3 text-left hover:bg-gray-50 rounded-md transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 group-hover:text-indigo-600">
                        {type.label}
                      </span>
                      <span className="text-xs text-indigo-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        Available
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 mt-1">
                      {type.description}
                    </span>
                  </Link>
                ) : (
                  <div className="flex flex-col px-3 py-3 text-left opacity-50 cursor-not-allowed">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-400">
                        {type.label}
                      </span>
                      <span className="text-xs text-gray-400 font-medium">
                        Coming Soon
                      </span>
                    </div>
                    <span className="text-xs text-gray-400 mt-1">
                      {type.description}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ValuationTypeDropdown;
